import {
  type Auth,
  type AuthProvider,
  type ConfirmationResult,
  FacebookAuthProvider,
  GoogleAuthProvider,
  type IdTokenResult,
  OAuthProvider,
  PhoneAuthProvider,
  type PopupRedirectResolver,
  type User,
  type UserCredential,
  type UserInfo,
  browserPopupRedirectResolver,
  getAdditionalUserInfo,
  getAuth,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  reauthenticateWithPopup,
  revokeAccessToken,
  signInWithCredential,
  signInWithCustomToken,
  unlink,
  updateProfile,
} from 'firebase/auth';

import { getApp } from './config';

let authInstance: Auth | null = null;

/**
 * Get the Auth instance (singleton). Importing this module pulls in
 * `firebase/auth` (+ `firebase/app` via {@link getApp}) only — NOT
 * `firebase/firestore` or `firebase/storage`.
 */
export function getAuthInstance(): Auth {
  if (authInstance) {
    return authInstance;
  }
  authInstance = getAuth(getApp());
  return authInstance;
}

// Re-export Auth instance accessor (legacy name)
export const auth = getAuthInstance;

// Re-export Auth functions. The link/unlink primitives back the
// account-linking ("+ Link" / "Unlink") flow on the Edit Profile page —
// `@ps/functions/firebase` is the single Firebase boundary, so components
// import these here rather than from `firebase/auth` directly.
//
// `OAuthProvider` + `signInWithCredential` back Sign in with Apple: on the web
// `OAuthProvider('apple.com')` goes through the same popup as Google/Facebook,
// and in the iOS shell the native sheet hands back an Apple ID token that
// `signInWithCredential` turns into a Firebase session. `revokeAccessToken` /
// `reauthenticateWithPopup` are the account-deletion side Apple requires.
export {
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithCredential,
  linkWithPopup,
  linkWithCredential,
  reauthenticateWithPopup,
  revokeAccessToken,
  getAdditionalUserInfo,
  updateProfile,
  unlink,
  PhoneAuthProvider,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  browserPopupRedirectResolver,
};

// Re-export Auth types
export type {
  User,
  UserInfo,
  UserCredential,
  Auth,
  AuthProvider,
  ConfirmationResult,
  PopupRedirectResolver,
  IdTokenResult,
};

// ── Controllable OAuth popup ─────────────────────────────────────────────────
//
// Firebase's `linkWithPopup` opens + owns its OAuth window internally, so a
// caller can't close that window from a custom "Cancel" (✕) button. To match
// PopshopRN's link-account UX (where ✕ closes the popup), we briefly intercept
// `window.open` for the duration of the popup launch to capture the `Window`
// handle Firebase opens, then restore the original immediately.
//
// We intercept `window.open` (a stable browser API) rather than wrapping the
// Firebase resolver — wrapping the resolver instance breaks Firebase's internal
// class machinery ("cls is not a constructor"). `browserPopupRedirectResolver`
// stays the resolver passed to `linkWithPopup`; we only watch which window it
// opens.
export interface ControllableOAuthPopup {
  /** Begin capturing the next `window.open` call (the OAuth popup). Call right
   *  before invoking `linkWithPopup`. Auto-restores after the first open or a
   *  short timeout, so it never leaves `window.open` patched. */
  beginCapture: () => void;
  /** Close the captured OAuth popup window AND restore `window.open` if this
   *  capture is still active (a cancel before the popup opened). No-op if the
   *  window is already gone. */
  close: () => void;
}

// Module-level guard so two overlapping captures (cancel an attempt whose popup
// never opened, then immediately retry) never NEST the `window.open` patch — a
// second `beginCapture` while one is active is a no-op, so the original
// `window.open` is always restored to the genuine browser implementation, never
// to a stale wrapper. Holds the active capture's `restore` so `close()` can
// reuse it.
let activeOAuthCapture: (() => void) | null = null;

export function createControllableOAuthPopup(): ControllableOAuthPopup {
  let captured: Window | null = null;
  let restore: (() => void) | null = null;

  return {
    beginCapture: () => {
      if (typeof window === 'undefined') return;
      // A capture is already installed (e.g. a prior attempt that hasn't
      // opened/timed out yet). Don't re-patch — that would nest wrappers and
      // leave `window.open` pointing at a stale one.
      if (activeOAuthCapture) return;

      const original = window.open.bind(window);
      restore = () => {
        if (activeOAuthCapture !== restore) return; // already restored
        activeOAuthCapture = null;
        restore = null;
        window.open = original;
      };
      activeOAuthCapture = restore;

      window.open = ((...args: Parameters<Window['open']>) => {
        const win = original(...args);
        captured = win;
        // The OAuth popup is the first window Firebase opens; restore right
        // after so we never affect any other `window.open` on the page.
        restore?.();
        return win;
      }) as Window['open'];

      // Safety net: if Firebase never opens a popup (e.g. blocked), restore.
      setTimeout(() => restore?.(), 3000);
    },
    close: () => {
      // Restore `window.open` if this capture is still patched (cancel before
      // the popup opened) so the patch never outlives the attempt.
      restore?.();
      try {
        captured?.close();
      } catch {
        /* window already gone */
      }
      captured = null;
    },
  };
}
